// src/components/FiltersToolbar.jsx - COMPACT SINGLE-LINE VERSION
import React, { useMemo } from 'react';

export default function FiltersToolbar({ 
  // Controlled props (no internal state)
  categoryMode = 'core',
  selectedCategory = 'all',
  selectedType = 'all',
  searchQuery = '',
  sortBy = 'total',
  
  // Dropdown options
  uniqueTypes = [],
  uniqueCoreCategories = [],
  uniqueClientCategories = [],
  
  // Change handlers
  onCategoryModeChange,
  onCategoryChange,
  onTypeChange,
  onSearchChange,
  onSortChange
}) {
  // Debug logging
  console.log('[FiltersToolbar Render]', {
    categoryMode,
    selectedCategory,
    uniqueClientCategories: uniqueClientCategories.length,
    uniqueCoreCategories: uniqueCoreCategories.length
  });

  // Determine which categories to show based on mode
  const categoriesToShow = useMemo(
    () => categoryMode === 'client' ? uniqueClientCategories : uniqueCoreCategories,
    [categoryMode, uniqueClientCategories, uniqueCoreCategories]
  );

  // Handle mode toggle
  const handleCategoryModeToggle = (e) => {
    const newMode = e.target.checked ? 'client' : 'core';
    onCategoryModeChange?.(newMode);
  };

  return (
    <div style={{
      backgroundColor: 'white',
      padding: '8px 16px',
      display: 'flex',
      gap: 12,
      alignItems: 'center',
      flexWrap: 'nowrap',
      overflow: 'hidden'
    }}>
      {/* Search Input - Compact */}
      <input
        type="text"
        placeholder="Search use cases..."
        value={searchQuery}
        onChange={(e) => onSearchChange?.(e.target.value)}
        style={{
          padding: '7px 10px',
          border: '1px solid #d1d5db',
          borderRadius: 6,
          fontSize: 13,
          width: 180,
          outline: 'none',
          transition: 'border-color 0.2s'
        }}
        onFocus={(e) => e.target.style.borderColor = '#18365E'}
        onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
      />

      {/* Type Filter */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <label style={{ fontSize: 13, fontWeight: 500, color: '#374151', whiteSpace: 'nowrap' }}>
          Type:
        </label>
        <select
          value={selectedType}
          onChange={(e) => onTypeChange?.(e.target.value)}
          style={{
            padding: '7px 10px',
            border: '1px solid #d1d5db',
            borderRadius: 6,
            fontSize: 13,
            backgroundColor: 'white',
            cursor: 'pointer',
            outline: 'none',
            minWidth: 110
          }}
        >
          {uniqueTypes.map(type => (
            <option key={type} value={type}>
              {type === 'all' ? 'All Types' : type}
            </option>
          ))}
        </select>
      </div>

      {/* Category Filter */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <label style={{ fontSize: 13, fontWeight: 500, color: '#374151', whiteSpace: 'nowrap' }}>
          Category:
        </label>
        <select
          value={selectedCategory}
          onChange={(e) => onCategoryChange?.(e.target.value)}
          style={{
            padding: '7px 10px',
            border: '1px solid #d1d5db',
            borderRadius: 6,
            fontSize: 13,
            backgroundColor: 'white',
            cursor: 'pointer',
            outline: 'none',
            minWidth: 160,
            maxWidth: 200
          }}
        >
          {categoriesToShow.length > 0 ? (
            categoriesToShow.map(cat => (
              <option key={cat} value={cat}>
                {cat === 'all' ? 
                  (categoryMode === 'client' ? 'All Client Categories' : 'All Core Categories') : 
                  cat
                }
              </option>
            ))
          ) : (
            <option value="all">
              {categoryMode === 'client' ? 'No client categories' : 'No core categories'}
            </option>
          )}
        </select>
      </div>

      {/* Category Mode Toggle (Checkbox) - Compact */}
      <label style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 5,
        fontSize: 13,
        color: '#374151',
        cursor: 'pointer',
        userSelect: 'none',
        padding: '4px 8px',
        borderRadius: 6,
        transition: 'background-color 0.2s',
        whiteSpace: 'nowrap'
      }}
      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
      >
        <input
          type="checkbox"
          checked={categoryMode === 'client'}
          onChange={handleCategoryModeToggle}
          style={{
            width: 14,
            height: 14,
            cursor: 'pointer',
            accentColor: '#18365E'
          }}
        />
        <span style={{ 
          fontWeight: categoryMode === 'client' ? 600 : 400,
          color: categoryMode === 'client' ? '#18365E' : '#374151'
        }}>
          Client Categories
        </span>
        {categoryMode === 'client' && uniqueClientCategories.length > 1 && (
          <span style={{
            fontSize: 11,
            color: '#6b7280',
            fontWeight: 400,
            backgroundColor: '#F4F7FA',
            padding: '1px 5px',
            borderRadius: 3
          }}>
            {uniqueClientCategories.length - 1}
          </span>
        )}
      </label>

      {/* Sort - Now inline */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
        <label style={{ fontSize: 13, fontWeight: 500, color: '#374151', whiteSpace: 'nowrap' }}>
          Sort by:
        </label>
        <select
          value={sortBy}
          onChange={(e) => onSortChange?.(e.target.value)}
          style={{
            padding: '7px 10px',
            border: '1px solid #d1d5db',
            borderRadius: 6,
            fontSize: 13,
            backgroundColor: 'white',
            cursor: 'pointer',
            outline: 'none',
            minWidth: 130
          }}
        >
          <option value="total">Total Score</option>
          <option value="value">Value Score</option>
          <option value="ease">Implementation Ease</option>
          <option value="category">Category</option>
          <option value="type">Type</option>
        </select>
      </div>
    </div>
  );
}